import { FieldNameSchema, isReferenceType, type FieldType, type InventoryResponse, type ProfileField } from "@starsach/shared-types";
import { detectedTimeZone } from "./preferences";

/** The wire names are snake_case identifiers; these are what a customer reads. */
export const FIELD_TYPE_LABELS: Record<FieldType, string> = {
  announcement: "Announcement",
  kv: "Key/value",
  voice: "Voice",
  hours: "Opening hours",
  menu: "Menu",
  queue: "Queue",
  flow: "Contact flow",
  prompt: "Prompt",
  hours_of_operation: "Hours of operation (Connect)",
  lex_bot: "Lex bot",
};

export const FIELD_TYPES = Object.keys(FIELD_TYPE_LABELS) as FieldType[];

/** Why a field of this type and name cannot be added right now, or undefined when it can. */
export function fieldProblem(type: FieldType, name: string, existing: ProfileField[], inventory: InventoryResponse | undefined): string | undefined {
  if (!FieldNameSchema.safeParse(name).success) return "A name is lower-case letters, digits and underscores, starting with a letter or digit: welcome, hold_1, tier_2.";
  const kind = FIELD_TYPE_LABELS[type].toLowerCase().replace(/ \(.*\)$/, "");
  if (existing.some((f) => f.type === type && f.name === name)) return `There is already ${/^[aeiou]/.test(kind) ? "an" : "a"} ${kind} called ${name}.`;
  if (isReferenceType(type) && !inventory?.items.some((i) => i.type === type)) return `This instance has no ${kind} to choose from yet.`;
  return undefined;
}

/**
 * A new field starts with a value that publishes as it is, so the first
 * version can go out before every detail is filled in: a reference points
 * at the first thing of that kind in the instance, hours are a working week
 * in the author's zone - the one chosen in Account › Preferences (#527), passed as `timeZone`, or the device's.
 */
export function newField(type: FieldType, name: string, inventory: InventoryResponse | undefined, timeZone: string = detectedTimeZone()): ProfileField {
  switch (type) {
    case "announcement":
      return { type, name, value: "Thank you for calling." };
    case "kv":
      return { type, name, value: "" };
    case "voice":
      return { type, name, value: "Joanna" };
    case "hours":
      return {
        type,
        name,
        value: {
          timezone: timeZone,
          weekly: [1, 2, 3, 4, 5].map((day) => ({ day, opens: "09:00", closes: "17:00" })),
          holidays: [],
        },
      };
    case "menu":
      return { type, name, value: [] };
    default:
      return { type, name, ref: inventory?.items.find((i) => i.type === type)?.name ?? "" };
  }
}
